import React, {ReactNode, useMemo, useState} from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import {
    Alert,
    Autocomplete,
    Box,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

interface DemoOption {
    id: string;
    name: string;
    group?: string;
    sector?: string;
    detail?: string;
    icon?: string;
    available?: boolean;
    disabled?: boolean;
}

interface ShowcaseRowProps {
    name: string;
    details: string;
    surface: string;
    owners: string[];
    children: ReactNode;
}

const SERVICES: DemoOption[] = [
    {id: "massage", name: "Classic massage · 60 min", group: "Associated services", available: true},
    {id: "facial", name: "Facial treatment · 45 min", group: "Associated services", available: true},
    {id: "manicure", name: "Manicure · 30 min", group: "Other services", available: false},
    {id: "yoga", name: "Private yoga · 60 min", group: "Other services", available: false},
];

const RESTAURANT_SERVICES: DemoOption[] = [
    {id: "restaurant-table", name: "Restaurant Table Reservation"},
    {id: "restaurant-breakfast", name: "Breakfast Table Reservation"},
];

const RESOURCES: DemoOption[] = [
    {id: "anna", name: "Anna Berger", detail: "Therapist", group: "Linked resources", available: true},
    {id: "room-1", name: "Treatment room 1", detail: "Room", group: "Linked resources", available: true},
    {id: "lisa", name: "Lisa Mayer", detail: "Therapist", group: "Not linked resources", available: false},
    {id: "room-2", name: "Treatment room 2", detail: "Room", group: "Not linked resources", available: true},
];

const TABLES: DemoOption[] = [
    {id: "panorama-1", name: "Table 1", detail: "2 seats", group: "Panorama Restaurant", available: true},
    {id: "panorama-2", name: "Table 2", detail: "4 seats", group: "Panorama Restaurant", available: false},
    {id: "garden-1", name: "Garden 1", detail: "4 seats", group: "Garden Restaurant", available: true},
    {id: "garden-2", name: "Garden 2", detail: "6 seats", group: "Garden Restaurant", available: true},
];

const BOOKING_ENGINE_SERVICES: DemoOption[] = [
    {id: "be-massage", name: "Classic massage", group: "Wellness / Massages", sector: "Wellness", available: true},
    {id: "be-stone", name: "Hot stone massage", group: "Wellness / Massages", sector: "Wellness", available: false},
    {id: "be-facial", name: "Facial treatment", group: "Wellness / Treatments", sector: "Wellness", available: true},
    {id: "be-yoga", name: "Private yoga", group: "Activities / Fitness", sector: "Activities", disabled: true},
    {id: "be-dinner", name: "Dinner reservation", group: "Restaurant / Dinner", sector: "Restaurant", disabled: true},
];

const SETTINGS_SERVICES: DemoOption[] = [
    {id: "settings-massage", name: "Classic massage", group: "Services in the selected sectors"},
    {id: "settings-facial", name: "Facial treatment", group: "Services in the selected sectors"},
    {id: "settings-breakfast", name: "Breakfast", group: "Other services"},
];

const COUNTRIES: DemoOption[] = [
    {id: "AT", name: "Austria"},
    {id: "DE", name: "Germany"},
    {id: "IT", name: "Italy"},
    {id: "CH", name: "Switzerland"},
];

const TIME_ZONES: Record<string, DemoOption[]> = {
    AT: [{id: "Europe/Vienna", name: "Europe/Vienna (UTC+02:00)"}],
    DE: [{id: "Europe/Berlin", name: "Europe/Berlin (UTC+02:00)"}],
    IT: [{id: "Europe/Rome", name: "Europe/Rome (UTC+02:00)"}],
    CH: [{id: "Europe/Zurich", name: "Europe/Zurich (UTC+02:00)"}],
};

const SEARCH_ENTITIES: DemoOption[] = [
    {id: "service-1", name: "Classic massage", detail: "Service"},
    {id: "resource-1", name: "Treatment room 1", detail: "Resource"},
    {id: "profile-1", name: "Sarah Parker", detail: "Profile"},
    {id: "profile-2", name: "Daniel Gruber", detail: "Profile"},
];

const COLUMNS: DemoOption[] = [
    {id: "name", name: "Name"},
    {id: "category", name: "Category"},
    {id: "status", name: "Status"},
    {id: "updated", name: "Last updated"},
];

const RESTAURANT_EVENTS: DemoOption[] = [
    {id: "BIRTHDAY", name: "Birthday"},
    {id: "ANNIVERSARY", name: "Anniversary"},
    {id: "BUSINESS_DINNER", name: "Business dinner"},
    {id: "OTHER", name: "Other occasion"},
];

const CONFLICT_TABLES: DemoOption[] = [
    {id: "conflict-1", name: "Table 3", detail: "Available", available: true},
    {id: "conflict-2", name: "Table 8", detail: "Sarah Parker · Room 204 · 19:00–20:00", available: false},
    {id: "conflict-3", name: "Garden 4", detail: "Daniel Gruber · 19:30–21:00", available: false},
];

const COMPACT_RESOURCES: DemoOption[] = [
    {id: "compact-1", name: "Treatment room 1"},
    {id: "compact-2", name: "Treatment room 2"},
    {id: "compact-3", name: "Anna Berger"},
    {id: "compact-4", name: "Lisa Mayer"},
    {id: "compact-5", name: "Pool area"},
];

const COUNTRY_FLAGS: DemoOption[] = [
    {id: "AT", name: "Austria", icon: "🇦🇹"},
    {id: "DE", name: "Germany", icon: "🇩🇪"},
    {id: "IT", name: "Italy", icon: "🇮🇹"},
    {id: "CH", name: "Switzerland", icon: "🇨🇭"},
];

const LANGUAGES: DemoOption[] = [
    {id: "de", name: "German"},
    {id: "en", name: "English"},
    {id: "it", name: "Italian"},
];

const OPTIONAL_LANGUAGES: DemoOption[] = [
    {id: "not-set", name: "(Not set)"},
    ...LANGUAGES,
];

const INTERNAL_LINKS: DemoOption[] = [
    {id: "overview", name: "Overview", detail: "Opens the guest app overview"},
    {id: "stays", name: "Stays", detail: "Opens the guest's current and previous stays"},
    {id: "booking", name: "RoomBookingScreen", detail: "Opens the room booking engine"},
];

const AVAILABILITY_RULES: DemoOption[] = [
    {id: "weekday", name: "Weekday spa hours", detail: "Mon–Fri · 09:00–18:00"},
    {id: "weekend", name: "Weekend spa hours", detail: "Sat–Sun · 10:00–16:00"},
    {id: "evening", name: "Evening treatment window", detail: "Daily · 18:00–21:00"},
];

const FINANCIAL_OPTIONS: DemoOption[] = [
    {id: "standard", name: "Standard rate", detail: "€120.00 · VAT 20% inclusive"},
    {id: "half-board", name: "Half-board rate", detail: "€165.00 · VAT 20% inclusive"},
    {id: "spa-package", name: "Spa package", detail: "€210.00 · VAT 20% exclusive"},
];

const isSameOption = (option: DemoOption, value: DemoOption) => option.id === value.id;

const optionLabel = (option: DemoOption) => [option.name, option.detail].filter(Boolean).join(" · ");

const ShowcaseRow = ({name, details, surface, owners, children}: ShowcaseRowProps) => (
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: {xs: "1fr", md: "210px minmax(340px, 480px) minmax(240px, 1fr)"},
            gap: {xs: 1.5, md: 3},
            alignItems: "start",
            py: 2.5,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            "&:last-child": {borderBottom: "none"},
        }}
    >
        <Box>
            <Typography fontWeight={600}>{name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                {details}
            </Typography>
        </Box>

        <Box sx={{minWidth: 0, width: "100%"}}>{children}</Box>

        <Stack gap={0.75} minWidth={0}>
            <Typography variant="body2" fontWeight={600}>{surface}</Typography>
            {owners.map(owner => (
                <Typography
                    key={owner}
                    variant="caption"
                    color="text.secondary"
                    sx={{fontFamily: "monospace", overflowWrap: "anywhere"}}
                >
                    {owner}
                </Typography>
            ))}
        </Stack>
    </Box>
);

const AutocompleteTestPage = () => {
    const [service, setService] = useState<DemoOption | null>(SERVICES[0]);
    const [dimmedRestaurantService, setDimmedRestaurantService] = useState<DemoOption | null>(RESTAURANT_SERVICES[0]);
    const [resources, setResources] = useState<DemoOption[]>([RESOURCES[0], RESOURCES[1]]);
    const [tables, setTables] = useState<DemoOption[]>([TABLES[0], TABLES[2]]);
    const [restaurantEvent, setRestaurantEvent] = useState<DemoOption | null>(RESTAURANT_EVENTS[0]);
    const [conflictTable, setConflictTable] = useState<DemoOption>(CONFLICT_TABLES[0]);
    const [bookingEngineServices, setBookingEngineServices] = useState<DemoOption[]>(BOOKING_ENGINE_SERVICES.slice(0, 2));
    const [compactResources, setCompactResources] = useState<DemoOption[]>(COMPACT_RESOURCES.slice(0, 4));
    const [allResources, setAllResources] = useState<DemoOption[]>(COMPACT_RESOURCES);
    const [editableResources, setEditableResources] = useState<DemoOption[]>(RESOURCES.slice(0, 2));
    const [settingsService, setSettingsService] = useState<DemoOption | null>(SETTINGS_SERVICES[0]);
    const [language, setLanguage] = useState<DemoOption>(LANGUAGES[0]);
    const [supportedLanguages, setSupportedLanguages] = useState<DemoOption[]>(LANGUAGES.slice(0, 2));
    const [chargeService, setChargeService] = useState<DemoOption | string | null>(SERVICES[0]);
    const [internalLink, setInternalLink] = useState<DemoOption | null>(INTERNAL_LINKS[0]);
    const [country, setCountry] = useState<DemoOption | null>(COUNTRIES[0]);
    const [timeZone, setTimeZone] = useState<DemoOption | null>(TIME_ZONES.AT[0]);
    const [flagCountries, setFlagCountries] = useState<DemoOption[]>(COUNTRY_FLAGS.slice(0, 2));
    const [entityQuery, setEntityQuery] = useState("");
    const [entity, setEntity] = useState<DemoOption | null>(null);
    const [availabilityRule, setAvailabilityRule] = useState<DemoOption | null>(null);
    const [filteredResources, setFilteredResources] = useState<DemoOption[]>(COMPACT_RESOURCES.slice(0, 2));
    const [financialOption, setFinancialOption] = useState<DemoOption | null>(FINANCIAL_OPTIONS[0]);
    const [tags, setTags] = useState<string[]>(["newsletter", "spa-interest"]);
    const [columns, setColumns] = useState<DemoOption[]>(COLUMNS.slice(0, 3));
    const [mockEdit, setMockEdit] = useState<{source: "booking-engine" | "resource"; name: string} | null>(null);
    const [heightValues, setHeightValues] = useState<Record<string, DemoOption | null>>({
        small: SERVICES[0],
        medium: SERVICES[1],
        settings: SETTINGS_SERVICES[0],
        fixed: RESTAURANT_EVENTS[0],
    });
    const [growingHeightTables, setGrowingHeightTables] = useState<DemoOption[]>(TABLES);

    const timeZoneOptions = country ? TIME_ZONES[country.id] ?? [] : [];
    const allResourcesSelected = allResources.length === COMPACT_RESOURCES.length;
    const entityOptions = useMemo(() => {
        const normalizedQuery = entityQuery.trim().toLowerCase();
        if (!normalizedQuery) return SEARCH_ENTITIES;

        return SEARCH_ENTITIES.filter(option =>
            `${option.name} ${option.detail ?? ""}`.toLowerCase().includes(normalizedQuery)
        );
    }, [entityQuery]);

    const toggleBookingEngineOptions = (options: DemoOption[]) => {
        const selectableOptions = options.filter(option => !option.disabled);
        const selectedIds = new Set(bookingEngineServices.map(option => option.id));
        const allSelected = selectableOptions.length > 0 && selectableOptions.every(option => selectedIds.has(option.id));
        const toggledIds = new Set(selectableOptions.map(option => option.id));

        setBookingEngineServices(allSelected
            ? bookingEngineServices.filter(option => !toggledIds.has(option.id))
            : [
                ...bookingEngineServices,
                ...selectableOptions.filter(option => !selectedIds.has(option.id)),
            ]
        );
    };

    // PageShell bounds the tab at 100dvh and clips it, so this page has to own its own scroller
    return (
        <Container maxWidth="xl" sx={{py: 4, overflowY: "auto"}}>
            <Card sx={{p: {xs: 2, sm: 4}, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)"}}>
                <Typography variant="h5" fontWeight={600}>
                    Autocomplete Playground
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.75, mb: 1.5}}>
                    A visual inventory of the autocomplete interaction and field-height patterns currently used across reservations, settings, shared forms, and the booking engine.
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{mb: 2}}>
                    <Chip size="small" label="174 production usages audited"/>
                    <Chip size="small" label="99 source files"/>
                    <Chip size="small" label="26 distinct interaction patterns"/>
                    <Chip size="small" label="5 field-height variants"/>
                </Stack>
                <Alert severity="info" variant="outlined" sx={{mb: 2.5}}>
                    Every example uses local mock data and React state. Repeated basic enum and entity pickers are grouped by behavior; opening or editing a value does not make an API request.
                </Alert>

                <Box
                    sx={{
                        display: {xs: "none", md: "grid"},
                        gridTemplateColumns: "210px minmax(340px, 480px) minmax(240px, 1fr)",
                        gap: 3,
                        pb: 1,
                        borderBottom: theme => `1px solid ${theme.palette.divider}`,
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                    }}
                >
                    <Box>Pattern</Box>
                    <Box>Mocked example</Box>
                    <Box>Production use / owner</Box>
                </Box>

                <ShowcaseRow
                    name="Field height variants"
                    details="The exact compact, default, settings, fixed reservation, and growing reservation geometries used in production."
                    surface="Theme defaults · settings forms · reservation dialogs"
                    owners={[
                        "crqlar-react-common/src/components/theme.tsx",
                        "system/resources/ResourceDetailsForm.tsx · system/services/ServiceEditDialog.tsx",
                        "reservations/reservationDialog/specializations/ReservationDetailsService.tsx",
                        "reservations/reservationDialog/specializations/ReservationDetailsAccommodation.tsx",
                        "reservations/reservationDialog/specializations/ReservationDetailsRestaurant.tsx",
                    ]}
                >
                    <Stack gap={2}>
                        <Stack gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                Small · compact theme spacing, reduced further on mobile
                            </Typography>
                            <Autocomplete
                                id="test-height-small"
                                size="small"
                                options={SERVICES}
                                value={heightValues.small}
                                onChange={(_, value) => setHeightValues(current => ({...current, small: value}))}
                                getOptionLabel={optionLabel}
                                isOptionEqualToValue={isSameOption}
                                renderInput={params => <TextField {...params} label="Compact field" size="small"/>}
                            />
                        </Stack>

                        <Stack gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                Medium · default theme spacing
                            </Typography>
                            <Autocomplete
                                id="test-height-medium"
                                options={SERVICES}
                                value={heightValues.medium}
                                onChange={(_, value) => setHeightValues(current => ({...current, medium: value}))}
                                getOptionLabel={optionLabel}
                                isOptionEqualToValue={isSameOption}
                                renderInput={params => <TextField {...params} label="Default field"/>}
                            />
                        </Stack>

                        <Stack gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                50px · settings form alignment
                            </Typography>
                            <Autocomplete
                                id="test-height-settings"
                                size="small"
                                options={SETTINGS_SERVICES}
                                value={heightValues.settings}
                                onChange={(_, value) => setHeightValues(current => ({...current, settings: value}))}
                                getOptionLabel={option => option.name}
                                isOptionEqualToValue={isSameOption}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Settings field"
                                        size="small"
                                        InputProps={{...params.InputProps, sx: {height: 50}}}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                Fixed 4em · reservation single value
                            </Typography>
                            <Autocomplete
                                id="test-height-reservation-fixed"
                                size="small"
                                sx={{
                                    "& fieldset": {height: "4em"},
                                    "& .MuiAutocomplete-input": {
                                        height: "2.2em",
                                        lineHeight: "2.2em",
                                    },
                                }}
                                options={RESTAURANT_EVENTS}
                                value={heightValues.fixed}
                                onChange={(_, value) => setHeightValues(current => ({...current, fixed: value}))}
                                getOptionLabel={option => option.name}
                                isOptionEqualToValue={isSameOption}
                                renderInput={params => <TextField {...params} label="Event" size="small"/>}
                            />
                        </Stack>

                        <Stack gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                Growing min 4em · reservation chips wrap onto more rows
                            </Typography>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                id="test-height-reservation-growing"
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                                        minHeight: "4em",
                                        paddingInline: "8px",
                                    },
                                }}
                                options={TABLES}
                                value={growingHeightTables}
                                onChange={(_, value) => setGrowingHeightTables(value)}
                                getOptionLabel={optionLabel}
                                isOptionEqualToValue={isSameOption}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Tables"
                                        size="small"
                                        InputLabelProps={{
                                            sx: {
                                                transform: "translate(14px, 16px) scale(1)",
                                                "&.MuiInputLabel-shrink": {
                                                    transform: "translate(14px, -11px) scale(0.75)",
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>
                </ShowcaseRow>

                <ShowcaseRow
                    name="Grouped service picker"
                    details="Single fixed-4em value; related services are ordered first and options carry the reservation availability tint."
                    surface="Reservation dialog · service reservation"
                    owners={["reservations/reservationDialog/specializations/ReservationDetailsService.tsx"]}
                >
                    <Autocomplete
                        id="test-reservation-service"
                        size="small"
                        sx={{
                            "& fieldset": {height: "4em"},
                            "& .MuiAutocomplete-input": {
                                height: "2.2em",
                                lineHeight: "2.2em",
                            },
                        }}
                        options={SERVICES}
                        value={service}
                        onChange={(_, value) => setService(value)}
                        groupBy={option => option.group ?? ""}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Chip
                                    size="small"
                                    label={option.name}
                                    sx={{bgcolor: option.available ? "success.light" : "error.light", color: "text.primary"}}
                                />
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label="Service" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Dimmed but editable service"
                    details="The restaurant specialization lowers the field opacity to de-emphasize its preselected service, while the dropdown remains interactive."
                    surface="Reservation dialog · restaurant reservation"
                    owners={["reservations/reservationDialog/specializations/ReservationDetailsRestaurant.tsx"]}
                >
                    <Autocomplete
                        id="test-dimmed-restaurant-service"
                        size="small"
                        sx={{
                            opacity: 0.6,
                            "& fieldset": {height: "4em"},
                            "& .MuiAutocomplete-input": {
                                height: "2.2em",
                                lineHeight: "2.2em",
                            },
                        }}
                        options={RESTAURANT_SERVICES}
                        value={dimmedRestaurantService}
                        onChange={(_, value) => setDimmedRestaurantService(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderInput={params => <TextField {...params} label="Service" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Resource assignment"
                    details="Multiple values; resources are grouped by service linkage, selected items remain visible but disabled in the menu, and options show mocked availability."
                    surface="Reservation dialog · service and accommodation reservations"
                    owners={[
                        "reservations/reservationDialog/specializations/ReservationDetailsService.tsx",
                        "reservations/reservationDialog/specializations/ReservationDetailsAccommodation.tsx",
                    ]}
                >
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="test-reservation-resources"
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                                minHeight: "4em",
                                paddingInline: "8px",
                            },
                        }}
                        options={RESOURCES}
                        value={resources}
                        onChange={(_, value) => setResources(value)}
                        groupBy={option => option.group ?? ""}
                        getOptionLabel={optionLabel}
                        getOptionDisabled={option => resources.some(selected => selected.id === option.id)}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id} sx={{gap: 1}}>
                                <Checkbox size="small" checked={selected}/>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: option.available ? "success.main" : "error.main"}}/>
                                <Box>{optionLabel(option)}</Box>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Resources"
                                size="small"
                                InputLabelProps={{
                                    sx: {
                                        transform: "translate(14px, 16px) scale(1)",
                                        "&.MuiInputLabel-shrink": {
                                            transform: "translate(14px, -11px) scale(0.75)",
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Restaurant table assignment"
                    details="Growing min-4em multi-select; open Tables to inspect restaurant headers, capacities, and mocked availability dots."
                    surface="Reservation dialog · restaurant reservation"
                    owners={["reservations/reservationDialog/specializations/ReservationDetailsRestaurant.tsx"]}
                >
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="test-restaurant-tables"
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                                minHeight: "4em",
                                paddingInline: "8px",
                            },
                        }}
                        options={TABLES}
                        value={tables}
                        onChange={(_, value) => setTables(value)}
                        groupBy={option => option.group ?? ""}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={isSameOption}
                        renderGroup={params => {
                            const groupOptions = TABLES.filter(option => option.group === params.group);
                            const selectedIds = new Set(tables.map(option => option.id));
                            const allSelected = groupOptions.every(option => selectedIds.has(option.id));
                            const someSelected = groupOptions.some(option => selectedIds.has(option.id));

                            return (
                                <li key={params.key}>
                                    <Box
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={event => {
                                            event.stopPropagation();
                                            const groupIds = new Set(groupOptions.map(option => option.id));
                                            setTables(allSelected
                                                ? tables.filter(option => !groupIds.has(option.id))
                                                : [...tables.filter(option => !groupIds.has(option.id)), ...groupOptions]
                                            );
                                        }}
                                        sx={{display: "flex", alignItems: "center", px: 1, py: 0.5, bgcolor: "action.selected", cursor: "pointer"}}
                                    >
                                        <Checkbox size="small" checked={allSelected} indeterminate={someSelected && !allSelected}/>
                                        <Typography variant="body2" fontWeight={600}>{params.group}</Typography>
                                    </Box>
                                    <ul style={{padding: 0}}>{params.children}</ul>
                                </li>
                            );
                        }}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id} sx={{gap: 1}}>
                                <Checkbox size="small" checked={selected}/>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: option.available ? "success.main" : "error.main"}}/>
                                <Box>{optionLabel(option)}</Box>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Tables"
                                size="small"
                                InputLabelProps={{
                                    sx: {
                                        transform: "translate(14px, 16px) scale(1)",
                                        "&.MuiInputLabel-shrink": {
                                            transform: "translate(14px, -11px) scale(0.75)",
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Restaurant event"
                    details="Fixed-4em translated enum picker for the occasion attached to a restaurant reservation."
                    surface="Reservation dialog · restaurant reservation"
                    owners={["reservations/reservationDialog/specializations/ReservationDetailsRestaurant.tsx"]}
                >
                    <Autocomplete
                        id="test-restaurant-event"
                        size="small"
                        sx={{
                            "& fieldset": {height: "4em"},
                            "& .MuiAutocomplete-input": {
                                height: "2.2em",
                                lineHeight: "2.2em",
                            },
                        }}
                        options={RESTAURANT_EVENTS}
                        value={restaurantEvent}
                        onChange={(_, value) => setRestaurantEvent(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderInput={params => <TextField {...params} label="Event" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Conflict-aware table reassignment"
                    details="Available tables sort first; occupied options explain the conflicting guest, room, and time."
                    surface="Floor plan · linked group conflict and restaurant action center"
                    owners={[
                        "reservations/floorPlan/v2/read/dialogs/GroupReservationConflictDialog.tsx",
                        "reservations/restaurantActionCenter/EventsUpdateDialogTables.tsx",
                    ]}
                >
                    <Autocomplete
                        disableClearable
                        id="test-conflict-table"
                        size="small"
                        options={CONFLICT_TABLES}
                        value={conflictTable}
                        onChange={(_, value) => setConflictTable(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id} sx={{gap: 1}}>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: option.available ? "success.main" : "error.main", flexShrink: 0}}/>
                                <Stack minWidth={0}>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color={option.available ? "text.secondary" : "error.main"}>
                                        {option.detail}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label="Replacement table" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Hierarchical booking-engine services"
                    details="Sector and category headers toggle whole groups; search spans names and hierarchy, incompatible sectors stay disabled, and availability chips expose separate edit and remove actions."
                    surface="Booking Engine · configuration · Basic"
                    owners={["bookingEngine/configurations/details/BookingEngineBasicTab.tsx"]}
                >
                    <Stack gap={1}>
                        <Stack direction="row" gap={2} flexWrap="wrap">
                            <Stack direction="row" gap={0.75} alignItems="center">
                                <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: "success.main"}}/>
                                <Typography variant="caption" color="text.secondary">Service is online bookable</Typography>
                            </Stack>
                            <Stack direction="row" gap={0.75} alignItems="center">
                                <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: "error.main"}}/>
                                <Typography variant="caption" color="text.secondary">Service is not online bookable</Typography>
                            </Stack>
                        </Stack>
                        <Alert severity="info" sx={{py: 0}}>
                            You can select services from only one sector
                        </Alert>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="test-booking-engine-services"
                        size="small"
                        options={BOOKING_ENGINE_SERVICES}
                        value={bookingEngineServices}
                        onChange={(_, value) => setBookingEngineServices(value)}
                        groupBy={option => option.group ?? ""}
                        getOptionLabel={option => option.name}
                        filterOptions={(options, {inputValue}) => {
                            const query = inputValue.trim().toLowerCase();
                            if (!query) return options;

                            return options.filter(option =>
                                `${option.name} ${option.sector ?? ""} ${option.group ?? ""}`.toLowerCase().includes(query)
                            );
                        }}
                        getOptionDisabled={option => Boolean(option.disabled)}
                        isOptionEqualToValue={isSameOption}
                        renderTags={(value, getTagProps) => value.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    color={option.available === true ? "success" : option.available === false ? "error" : "default"}
                                    label={
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                            <Box component="span">{option.name}</Box>
                                            <Tooltip title="Edit service">
                                                <IconButton
                                                    size="small"
                                                    onMouseDown={event => {
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                    }}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        setMockEdit({source: "booking-engine", name: option.name});
                                                    }}
                                                    sx={{p: 0.25}}
                                                >
                                                    <EditIcon sx={{fontSize: "0.9rem"}}/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            );
                        })}
                        renderGroup={params => {
                            const groupOptions = BOOKING_ENGINE_SERVICES.filter(option => option.group === params.group);
                            const sector = groupOptions[0]?.sector ?? "Other";
                            const sectorOptions = BOOKING_ENGINE_SERVICES.filter(option => option.sector === sector);
                            const firstSectorGroup = sectorOptions[0]?.group === params.group;
                            const selectedIds = new Set(bookingEngineServices.map(option => option.id));
                            const selectableGroupOptions = groupOptions.filter(option => !option.disabled);
                            const selectableSectorOptions = sectorOptions.filter(option => !option.disabled);
                            const allGroupSelected = selectableGroupOptions.length > 0 && selectableGroupOptions.every(option => selectedIds.has(option.id));
                            const someGroupSelected = selectableGroupOptions.some(option => selectedIds.has(option.id));
                            const allSectorSelected = selectableSectorOptions.length > 0 && selectableSectorOptions.every(option => selectedIds.has(option.id));
                            const someSectorSelected = selectableSectorOptions.some(option => selectedIds.has(option.id));

                            return (
                                <li key={params.key}>
                                    {firstSectorGroup && (
                                        <Box
                                            onMouseDown={event => event.preventDefault()}
                                            onClick={event => {
                                                event.stopPropagation();
                                                toggleBookingEngineOptions(sectorOptions);
                                            }}
                                            sx={{display: "flex", alignItems: "center", px: 1, py: 0.5, bgcolor: "grey.600", color: "common.white", cursor: selectableSectorOptions.length ? "pointer" : "default"}}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={allSectorSelected}
                                                indeterminate={someSectorSelected && !allSectorSelected}
                                                disabled={!selectableSectorOptions.length}
                                                sx={{color: "common.white", "&.Mui-checked, &.MuiCheckbox-indeterminate": {color: "common.white"}}}
                                            />
                                            <Typography variant="body2" fontWeight={700}>Sector [{sector}]</Typography>
                                        </Box>
                                    )}
                                    <Box
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={event => {
                                            event.stopPropagation();
                                            toggleBookingEngineOptions(groupOptions);
                                        }}
                                        sx={{display: "flex", alignItems: "center", px: 1, py: 0.5, bgcolor: "grey.200", cursor: selectableGroupOptions.length ? "pointer" : "default"}}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={allGroupSelected}
                                            indeterminate={someGroupSelected && !allGroupSelected}
                                            disabled={!selectableGroupOptions.length}
                                        />
                                        <Typography variant="body2" fontWeight={600}>
                                            Service Category [{params.group.split(" / ")[1] ?? params.group}]
                                        </Typography>
                                    </Box>
                                    <ul style={{padding: 0, margin: 0, listStyle: "none"}}>{params.children}</ul>
                                </li>
                            );
                        }}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id} sx={{gap: 1}}>
                                <Checkbox size="small" checked={selected}/>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: option.available === false ? "error.main" : "success.main"}}/>
                                {option.name}
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} placeholder="Search services, sectors, or categories" size="small"/>}
                    />
                        {mockEdit?.source === "booking-engine" && (
                            <Typography variant="caption" color="text.secondary">
                                Mock edit opened for {mockEdit.name}
                            </Typography>
                        )}
                    </Stack>
                </ShowcaseRow>

                <ShowcaseRow
                    name="Compact toolbar filter"
                    details="A dense checkbox picker that limits visible chips and summarizes the remaining selection as +N."
                    surface="Insights headers and compact content/list filters"
                    owners={[
                        "insights/components/ResourcePicker.tsx",
                        "system/content/ContentList.tsx",
                    ]}
                >
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={2}
                        id="test-compact-resources"
                        size="small"
                        options={COMPACT_RESOURCES}
                        value={compactResources}
                        onChange={(_, value) => setCompactResources(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id}>
                                <Checkbox size="small" checked={selected}/>
                                {option.name}
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label="Resources" placeholder="All" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="All selected collapses to ‘All’"
                    details="When every resource is selected, individual chips disappear and the placeholder summarizes the state; reopening the menu still shows each checked option."
                    surface="Insights headers · resource filters"
                    owners={["insights/components/ResourcePicker.tsx"]}
                >
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={2}
                        id="test-all-resources"
                        size="small"
                        options={COMPACT_RESOURCES}
                        value={allResources}
                        onChange={(_, value) => setAllResources(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id}>
                                <Checkbox size="small" checked={selected}/>
                                {option.name}
                            </Box>
                        )}
                        renderTags={(value, getTagProps) => allResourcesSelected
                            ? null
                            : value.map((option, index) => {
                                const {key, ...tagProps} = getTagProps({index});
                                return <Chip key={key} {...tagProps} size="small" label={option.name}/>;
                            })
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Resources"
                                placeholder={allResourcesSelected || allResources.length === 0 ? "All" : ""}
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <Box sx={{display: "flex", alignItems: "center", pl: 0.5, color: "text.secondary"}}>
                                                <MeetingRoomOutlinedIcon fontSize="small"/>
                                            </Box>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Settings assignment"
                    details="Single grouped search used when attaching an existing entity from a settings detail tab."
                    surface="Settings · Resources / Services · assignment dialogs"
                    owners={[
                        "system/resources/ResourceServicesTab.tsx",
                        "system/services/ServiceBookingEnginesTab.tsx",
                    ]}
                >
                    <Autocomplete
                        id="test-settings-service"
                        size="small"
                        options={SETTINGS_SERVICES}
                        value={settingsService}
                        onChange={(_, value) => setSettingsService(value)}
                        groupBy={option => option.group ?? ""}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        noOptionsText="No services found"
                        renderInput={params => <TextField {...params} placeholder="Search service" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Editable selected chips and options"
                    details="Linked entities can be edited without changing the selection: each chip combines edit and remove controls, and every dropdown option repeats the edit action."
                    surface="Settings · Service details · linked resources"
                    owners={["system/services/ServiceDetailsForm.tsx"]}
                >
                    <Stack gap={0.75}>
                        <Autocomplete
                            multiple
                            id="test-editable-resources"
                            size="small"
                            options={RESOURCES}
                            value={editableResources}
                            onChange={(_, value) => setEditableResources(value)}
                            groupBy={option => option.group ?? ""}
                            getOptionLabel={option => option.name}
                            isOptionEqualToValue={isSameOption}
                            renderTags={(value, getTagProps) => value.map((option, index) => {
                                const {key, ...tagProps} = getTagProps({index});
                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        label={option.name}
                                        deleteIcon={
                                            <Box component="span" sx={{display: "inline-flex", alignItems: "center", gap: 1}}>
                                                <EditIcon
                                                    sx={{fontSize: 18, cursor: "pointer"}}
                                                    onMouseDown={event => {
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                    }}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        setMockEdit({source: "resource", name: option.name});
                                                    }}
                                                />
                                                <CancelIcon sx={{fontSize: 20}}/>
                                            </Box>
                                        }
                                        sx={{
                                            m: 0.5,
                                            height: 36,
                                            "& .MuiChip-label": {px: 1.5},
                                            "& .MuiChip-deleteIcon": {mr: 1, ml: 0.75},
                                        }}
                                    />
                                );
                            })}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id} sx={{justifyContent: "space-between", gap: 1}}>
                                    <Stack minWidth={0}>
                                        <Typography variant="body2">{option.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
                                    </Stack>
                                    <Tooltip title={`Edit ${option.name}`}>
                                        <IconButton
                                            size="small"
                                            onMouseDown={event => {
                                                event.stopPropagation();
                                                event.preventDefault();
                                            }}
                                            onClick={event => {
                                                event.stopPropagation();
                                                setMockEdit({source: "resource", name: option.name});
                                            }}
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                            renderInput={params => <TextField {...params} label="Linked resources" size="small"/>}
                        />
                        {mockEdit?.source === "resource" && (
                            <Typography variant="caption" color="text.secondary">
                                Mock edit opened for {mockEdit.name}
                            </Typography>
                        )}
                    </Stack>
                </ShowcaseRow>

                <ShowcaseRow
                    name="Required non-clearable value"
                    details="The current value may be replaced but cannot be cleared, keeping required configuration valid."
                    surface="Organization language and profile-list selectors"
                    owners={[
                        "system/organization/OrganizationDetails.tsx",
                        "crqlar-react-common/src/components/profile/ProfileList.tsx",
                    ]}
                >
                    <Autocomplete
                        disableClearable
                        id="test-required-language"
                        size="small"
                        options={LANGUAGES}
                        value={language}
                        onChange={(_, value) => setLanguage(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderInput={params => <TextField {...params} required label="Interface language" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Chips-only optional settings value"
                    details="A multi-value settings field has an explicit ‘(Not set)’ option, no bulk-clear action, and a zero-width typing input so selection happens through the popup."
                    surface="Settings · Organization · supported languages"
                    owners={["system/organization/OrganizationDetails.tsx"]}
                >
                    <Autocomplete
                        multiple
                        disableClearable
                        id="test-supported-languages"
                        size="small"
                        options={OPTIONAL_LANGUAGES}
                        value={supportedLanguages}
                        onChange={(_, value) => setSupportedLanguages(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        sx={{
                            "& .MuiAutocomplete-input": {
                                flexGrow: 0,
                                width: 0,
                                minWidth: 0,
                                p: "0 !important",
                            },
                        }}
                        renderInput={params => <TextField {...params} label="Supported languages" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Select existing or type new"
                    details="freeSolo single selection accepts a catalog service or a new charge description typed by the user."
                    surface="Settings · Billing · folio charge dialog"
                    owners={["system/billing/folio/SimpleChargeDialog.tsx"]}
                >
                    <Autocomplete<DemoOption, false, false, true>
                        freeSolo
                        id="test-creatable-service"
                        size="small"
                        options={SERVICES}
                        value={chargeService}
                        onChange={(_, value) => setChargeService(value)}
                        getOptionLabel={option => typeof option === "string" ? option : option.name}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Stack>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">Existing catalog service</Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                required
                                label="Service name"
                                helperText="Choose an existing service or type a new description"
                                size="small"
                            />
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Rich title and description options"
                    details="Options explain their destination, and the selected option keeps its description as helper text."
                    surface="Shared content-data editor and marketing automation configuration"
                    owners={[
                        "crqlar-react-common/src/components/ContentDataTagsEditor.tsx",
                        "marketing/automations/nodeEditors/FormSubmittedEditor.tsx",
                    ]}
                >
                    <Autocomplete
                        id="test-rich-options"
                        size="small"
                        options={INTERNAL_LINKS}
                        value={internalLink}
                        onChange={(_, value) => setInternalLink(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Stack>
                                    <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField {...params} label="Internal screen" helperText={internalLink?.detail} size="small"/>
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Dependent address fields"
                    details="The time-zone options depend on the selected country and are disabled until a country exists."
                    surface="Shared address forms · organization and profile settings"
                    owners={["crqlar-react-common/src/components/AddressField.tsx"]}
                >
                    <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                        <Autocomplete
                            id="test-address-country"
                            size="small"
                            options={COUNTRIES}
                            value={country}
                            onChange={(_, value) => {
                                setCountry(value);
                                setTimeZone(value ? (TIME_ZONES[value.id] ?? [])[0] ?? null : null);
                            }}
                            getOptionLabel={option => option.name}
                            isOptionEqualToValue={isSameOption}
                            renderInput={params => <TextField {...params} label="Country" size="small"/>}
                            sx={{flex: 1}}
                        />
                        <Autocomplete
                            id="test-address-timezone"
                            size="small"
                            options={timeZoneOptions}
                            value={timeZone}
                            onChange={(_, value) => setTimeZone(value)}
                            getOptionLabel={option => option.name}
                            isOptionEqualToValue={isSameOption}
                            disabled={!country}
                            noOptionsText={country ? "No time zones found" : "Select a country first"}
                            renderInput={params => <TextField {...params} label="Time zone" size="small"/>}
                            sx={{flex: 1.4}}
                        />
                    </Stack>
                </ShowcaseRow>

                <ShowcaseRow
                    name="Country flags"
                    details="Searchable multi-country selection renders a flag in both options and selected chips."
                    surface="Insights country filters"
                    owners={["insights/components/NewCountryFlagPicker.tsx"]}
                >
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="test-country-flags"
                        size="small"
                        options={COUNTRY_FLAGS}
                        value={flagCountries}
                        onChange={(_, value) => setFlagCountries(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id} sx={{gap: 1}}>
                                <Box component="span" sx={{fontSize: 20, lineHeight: 1}}>{option.icon}</Box>
                                {option.name}
                            </Box>
                        )}
                        renderTags={(value, getTagProps) => value.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return <Chip key={key} {...tagProps} size="small" label={`${option.icon} ${option.name}`}/>;
                        })}
                        renderInput={params => <TextField {...params} placeholder="Filter by country…" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Remote entity search"
                    details="Production disables client filtering and loads matching entities remotely; this version filters only local mock data."
                    surface="Shared service/resource info rows and filter inputs"
                    owners={[
                        "crqlar-react-common/src/components/ServiceInfoRow.tsx",
                        "crqlar-react-common/src/components/ResourceInfoRow.tsx",
                    ]}
                >
                    <Autocomplete
                        id="test-entity-search"
                        size="small"
                        options={entityOptions}
                        value={entity}
                        inputValue={entityQuery}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input" || reason === "clear") setEntityQuery(value);
                        }}
                        onChange={(_, value) => {
                            setEntity(value);
                            setEntityQuery(value?.name ?? "");
                        }}
                        filterOptions={options => options}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Stack>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label="Find entity" placeholder="Type a name" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Async loading state"
                    details="The popup and input adornment both communicate that remote options are still being fetched."
                    surface="Audit logs, content-data widgets, and shared profile/service/resource searches"
                    owners={[
                        "system/logs/LogsList.tsx",
                        "crqlar-react-common/src/components/ContentDataTagsEditor.tsx",
                        "crqlar-react-common/src/components/ProfileInfoRow.tsx",
                    ]}
                >
                    <Box sx={{minHeight: 110}}>
                        <Autocomplete<DemoOption>
                            open
                            disablePortal
                            loading
                            id="test-async-loading"
                            size="small"
                            options={[]}
                            value={null}
                            loadingText="Loading matching entities…"
                            getOptionLabel={option => option.name}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Profile"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                <CircularProgress color="inherit" size={18}/>
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                </ShowcaseRow>

                <ShowcaseRow
                    name="Disabled / externally managed"
                    details="A selected value remains readable while editing is blocked by permissions or external ownership."
                    surface="Settings details, integration mappings, billing, and managed profile fields"
                    owners={[
                        "system/mapping/MappingCodesDetails.tsx",
                        "system/billing/paymentMethod/PaymentMethodDetails.tsx",
                        "system/profile/profileDetailsForm/ProfileDetailsForm.tsx",
                    ]}
                >
                    <Autocomplete
                        disabled
                        id="test-disabled-value"
                        size="small"
                        options={LANGUAGES}
                        value={LANGUAGES[0]}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderInput={params => <TextField {...params} label="Managed language" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Transient optional selector"
                    details="Uncommitted text clears on blur or Escape; only a selected rule becomes part of the configuration."
                    surface="Service availability bindings and marketing automation filters"
                    owners={[
                        "system/services/AvailabilitySection.tsx",
                        "marketing/automations/nodeEditors/ReservationCreatedEditor.tsx",
                    ]}
                >
                    <Autocomplete
                        clearOnBlur
                        clearOnEscape
                        id="test-transient-rule"
                        size="small"
                        options={AVAILABILITY_RULES}
                        value={availabilityRule}
                        onChange={(_, value) => setAvailabilityRule(value)}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Stack>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField {...params} label="Availability rule (optional)" helperText="Blur or press Escape to discard typed text" size="small"/>
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Hide selected options"
                    details="Selected values remain as chips but disappear from the dropdown, preventing accidental duplicate assignment."
                    surface="Resource categories and restaurant action-center table assignment"
                    owners={[
                        "system/resources/ResourceDetailsForm.tsx",
                        "reservations/restaurantActionCenter/EventsUpdateDialogTables.tsx",
                    ]}
                >
                    <Autocomplete
                        multiple
                        filterSelectedOptions
                        disableCloseOnSelect
                        id="test-filter-selected-options"
                        size="small"
                        options={COMPACT_RESOURCES}
                        value={filteredResources}
                        onChange={(_, value) => setFilteredResources(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id}>
                                <Checkbox size="small" checked={selected}/>
                                {option.name}
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label="Resources" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Rate, tax, and currency choice"
                    details="Financial options keep the compact selector but include price and VAT context in each label."
                    surface="Folio items, amount fields, rate dialogs, and tax-rate pickers"
                    owners={[
                        "crqlar-react-common/src/components/AmountField.tsx",
                        "crqlar-react-common/src/components/TaxRatePicker.tsx",
                        "crqlar-react-common/src/components/PickRateDialog.tsx",
                        "crqlar-react-common/src/components/FolioItemDialog.tsx",
                    ]}
                >
                    <Autocomplete
                        disablePortal
                        id="test-financial-option"
                        size="small"
                        options={FINANCIAL_OPTIONS}
                        value={financialOption}
                        onChange={(_, value) => setFinancialOption(value)}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Stack>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label="Rate / tax" size="small"/>}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Free-form tags"
                    details="Multiple freeSolo text or numeric values become individually removable chips when Enter is pressed, without a bulk-clear icon."
                    surface="Profile opt-in IDs, connector mappings, and text/numeric filter values"
                    owners={[
                        "system/profile/profileDetailsForm/ProfileDetailsForm.tsx",
                        "crqlar-react-common/src/components/filters/input/FilterNumericInput.tsx",
                        "crqlar-react-common/src/components/filters/input/FitlerTextInput.tsx",
                    ]}
                >
                    <Autocomplete<string, true, false, true>
                        multiple
                        freeSolo
                        clearIcon={false}
                        id="test-free-form-tags"
                        size="small"
                        options={["newsletter", "restaurant-interest", "spa-interest"]}
                        value={tags}
                        onChange={(_, value) => setTags(value)}
                        renderTags={(value, getTagProps) => value.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return <Chip key={key} {...tagProps} size="small" label={option}/>;
                        })}
                        renderInput={params => (
                            <TextField {...params} placeholder="Add an opt-in ID and press Enter" size="small"/>
                        )}
                    />
                </ShowcaseRow>

                <ShowcaseRow
                    name="Checkbox multi-select"
                    details="The menu stays open while columns are toggled; selected items are summarized as chips."
                    surface="Shared lists · column and print-column selection"
                    owners={[
                        "crqlar-react-common/src/components/CList/ColumnSelect.tsx",
                        "common/serverTable/print/ServerTablePrintColumnPicker.tsx",
                    ]}
                >
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="test-column-select"
                        size="small"
                        options={COLUMNS}
                        value={columns}
                        onChange={(_, value) => setColumns(value)}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={isSameOption}
                        renderOption={(props, option, {selected}) => (
                            <Box component="li" {...props} key={option.id}>
                                <Checkbox size="small" checked={selected}/>
                                {option.name}
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} placeholder="Select columns" size="small"/>}
                    />
                </ShowcaseRow>
            </Card>
        </Container>
    );
};

export default AutocompleteTestPage;
